import { formatThousands } from '@noor/lib';
import type { OrderPoint, OrderProductItem } from '../model/order-schema';

/**
 * Product-table presentation, ported from cpanel `ProductsInfo`. Weight arrives
 * in grams (→ kg), dimensions in cm (`HxWxL`), price is `quantity × unit`. Each
 * point's `products.items` becomes a flat, table-ready row set.
 */

const GRAMS_PER_KG = 1000;
const PLACEHOLDER = '—';

function productImage(item: OrderProductItem): string | null {
  for (const value of [
    item.image_url,
    item.image_path,
    item.photo_url,
    item.photo,
    item.image,
  ]) {
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

/** Grams → kilograms string (cpanel `formatWeight`). */
export function formatProductWeight(weight: number | null | undefined): string {
  if (weight == null || weight === 0) return PLACEHOLDER;
  return String(weight / GRAMS_PER_KG);
}

/** `HxWxL` cm string, zero-filling missing axes (cpanel `formatDimensions`). */
export function formatProductDimensions(
  height: number | null | undefined,
  width: number | null | undefined,
  length: number | null | undefined,
): string {
  if (!height && !width && !length) return PLACEHOLDER;
  return `${height ?? 0}x${width ?? 0}x${length ?? 0}`;
}

/** `qty × <thousands> сум` string (cpanel `formatPrice`); unit appended by UI. */
export function formatProductLinePrice(
  quantity: number | null | undefined,
  pricePerUnit: number | null | undefined,
): string {
  if (!quantity || !pricePerUnit) return PLACEHOLDER;
  return `${quantity}× ${formatThousands(pricePerUnit)}`;
}

export interface ProductRow {
  id: string;
  address: string;
  name: string;
  imageUrl: string | null;
  weight: string;
  dimensions: string;
  quantity: string;
  /** `qty × unit` line (no currency unit; UI appends). */
  price: string;
}

/** Flatten one route point's `products.items` into table rows. */
export function pointProductRows(point: OrderPoint, groupKey: string): ProductRow[] {
  const items = point.products?.items ?? [];
  return items.map((item: OrderProductItem, i) => ({
    id: `${groupKey}-${i}`,
    address: point.address ?? PLACEHOLDER,
    name: item.name ?? PLACEHOLDER,
    imageUrl: productImage(item),
    weight: formatProductWeight(item.weight),
    dimensions: formatProductDimensions(item.height, item.width, item.length),
    quantity: item.quantity != null ? String(item.quantity) : PLACEHOLDER,
    price: formatProductLinePrice(item.quantity, item.price_per_unit),
  }));
}

export interface ProductGroup {
  /** 'origin' | 'destination', for the table title. */
  kind: 'origin' | 'destination';
  /** 1-based index within its kind (cpanel "Товары N"). */
  index: number;
  rows: ProductRow[];
}

/**
 * Build per-point product groups (origin first, then destination), skipping
 * points with no items. Returns `[]` when the order carries no products — the
 * UI then hides the whole section (cpanel returned `null`).
 */
export function buildProductGroups(
  origin: OrderPoint[] | null | undefined,
  destination: OrderPoint[] | null | undefined,
): ProductGroup[] {
  const groups: ProductGroup[] = [];
  (origin ?? []).forEach((p, i) => {
    const rows = pointProductRows(p, `origin-${i}`);
    if (rows.length > 0) groups.push({ kind: 'origin', index: i + 1, rows });
  });
  (destination ?? []).forEach((p, i) => {
    const rows = pointProductRows(p, `destination-${i}`);
    if (rows.length > 0) groups.push({ kind: 'destination', index: i + 1, rows });
  });
  return groups;
}
